import { MessagingChannelTargetType } from '@treadinc/horizon-api-spec';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { StatusTabValue } from '~components/Filters/OrdersStatusFilter';
import { DispatchURLParams } from '~constants/dispatch';
import { useJob } from '~hooks/useJob/useJob';
import { MessagingChannelTargetReference } from '~hooks/useMessaging/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import { DispatchView } from '~pages/Dispatch/components/constants';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { PendingDeepLink } from '~store/OrdersDispatchStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// The deep link sets the Orders tab states; any other tab re-syncs and drops the link.
const DISPATCH_ORDERS_PATH = `/${ROUTES.dispatch.base}?${new URLSearchParams({
  [DispatchURLParams.VIEW]: DispatchView.ORDERS,
  [DispatchURLParams.TAB]: StatusTabValue.ORDERS,
})}`;

export const useOpenMessagingChannelTarget = (
  target: MessagingChannelTargetReference,
) => {
  const { ordersDispatchStore, userStore } = useStores();
  const navigate = useNavigate();
  const { getJobById } = useJob();
  const { getOrderById } = useOrders();
  const [isOpening, setIsOpening] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const resolveDeepLink = async (): Promise<PendingDeepLink | null> => {
    if (target.type === MessagingChannelTargetType.ORDER) {
      return { order: await getOrderById(target.id) };
    }

    // getJobById reports its own failure and resolves empty instead of throwing.
    const job = await getJobById(target.id, getEffectiveUserCompanyId(userStore));
    if (!job) {
      return null;
    }

    const order = await getOrderById(job.order.id);

    return { job: { id: job.id, jobStartAt: job.jobStartAt }, order };
  };

  const openTarget = async () => {
    setIsOpening(true);

    let deepLink: PendingDeepLink | null = null;
    try {
      deepLink = await resolveDeepLink();
    } catch {
      // The failed fetch has already shown its toast, and the thread stays open.
    }

    if (!isMountedRef.current) {
      return;
    }

    setIsOpening(false);

    if (deepLink) {
      ordersDispatchStore.setPendingDeepLink(deepLink);
      navigate(DISPATCH_ORDERS_PATH);
    }
  };

  return { isOpening, openTarget };
};
